import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";

const propertyEmailPath = (propertyId: number) =>
  `/portal/v1/communication_defaults/properties/${propertyId}/email`;

const PROPERTY_DEFAULT_EMAIL_KEY = (propertyId: number) =>
  ["PROPERTY_DEFAULT_EMAIL", propertyId] as const;

interface PropertyEmailOption {
  mailbox_id: string;
  email: string;
  display_name: string | null;
}

interface PropertyDefaultEmail {
  property_id: number;
  available_emails: PropertyEmailOption[];
  default_mailbox_id: string | null;
  resolved_email: string | null;
}

const propertyDefaultEmailApi = {
  get: async (propertyId: number): Promise<PropertyDefaultEmail> => {
    return (await instance.get(propertyEmailPath(propertyId))).data;
  },

  set: async (
    propertyId: number,
    mailboxId: string | null
  ): Promise<PropertyDefaultEmail> => {
    return (
      await instance.patch(propertyEmailPath(propertyId), {
        mailbox_id: mailboxId
      })
    ).data;
  }
};

export const usePropertyDefaultEmail = (
  propertyId: number,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: PROPERTY_DEFAULT_EMAIL_KEY(propertyId),
    queryFn: () => propertyDefaultEmailApi.get(propertyId),
    enabled: enabled && propertyId > 0,
    retry: false
  });

export const useSetPropertyDefaultEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PROPERTY_DEFAULT_EMAIL", "UPDATE"],
    mutationFn: ({
      propertyId,
      mailboxId
    }: {
      propertyId: number;
      mailboxId: string | null;
    }) => propertyDefaultEmailApi.set(propertyId, mailboxId),
    onSuccess: (_data, { propertyId }) => {
      queryClient.invalidateQueries({
        queryKey: PROPERTY_DEFAULT_EMAIL_KEY(propertyId)
      });
    }
  });
};
